import React, { useState } from "react";
import { Copy, Check } from "lucide-react";

const highlightCode = (code) => {
  if (!code) return "";
  return code
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/(['"`])(.*?)\1/g, '<span class="text-[#17A257]">$1$2$1</span>')
    .replace(
      /\b(import|from|const|let|var|return|await|async|fetch|requests|post|get|print|method|headers|body|json|stringify|X|H|d|F|FormData|append|open|blob|arrayBuffer)\b/g,
      '<span class="text-[#BC39B7]">$1</span>'
    )
    .replace(/"(\w+)":/g, '<span class="text-[#0070C1]">"$1"</span>:')
    .replace(/(^|\s)(#.*|\/\/.*)/g, '$1<span class="text-[#7E726B] opacity-60">$2</span>');
};

const CodeBlock = ({ code, id, language = "", onCopy, copyStatus }) => {
  const [internalCopied, setInternalCopied] = useState(false);

  const isCopied = copyStatus && id ? copyStatus[id] : internalCopied;

  const handleCopy = async () => {
    if (onCopy && id) {
      onCopy(code, id);
      return;
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = code;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        textArea.remove();
      }
      setInternalCopied(true);
      setTimeout(() => setInternalCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy code:", err);
    }
  };

  return (
    <div className="relative group w-full">
      <div className="border border-[#E0E4E2] rounded-2xl bg-[#FFFFFF] overflow-hidden transition-all duration-300 hover:border-[#17A257]/40 hover:shadow-md">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E0E4E2] bg-[#F9FAFB]">
          <div className="flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]"></div>
            </div>
            <span className="ml-2 text-[10px] font-black text-[#7E726B] uppercase tracking-widest">
              {language || "code"}
            </span>
          </div>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 p-1.5 px-3 rounded-lg hover:bg-[#E7F6EC] hover:text-[#17A257] text-[#1D2129] transition-all duration-200"
            aria-label="Copy code"
          >
            {isCopied ? (
              <>
                <Check className="w-[14px] h-[14px] text-[#17A257]" />
                <span className="text-xs font-bold text-[#17A257]">Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-[14px] h-[14px]" />
                <span className="text-xs font-bold">Copy</span>
              </>
            )}
          </button>
        </div>
        <div className="p-6 overflow-x-auto bg-[#FDFDFD]">
          <pre className="text-[#1D2129] text-sm leading-relaxed font-mono whitespace-pre">
            <code dangerouslySetInnerHTML={{ __html: highlightCode(code) }} />
          </pre>
        </div>
      </div>
    </div>
  );
};

export default CodeBlock;
